import { fileToBase64 } from '@eneco/common/file';
import { postCustomerInquiry, postInquiry } from '@eneco/dc/services';
import useDC from '@eneco/dc/useDC';
import type { Forms_CustomerInquiryRequest } from '@monorepo-types/dc';

import type { FormFields } from './types';

export const usePostInquiry = () => {
  const { businessUnit, label } = useDC();

  return async (input: FormFields, files: File[], subject: string) => {
    const attachments = files
      ? await Promise.all(
          files.map(async file => {
            const content = await fileToBase64(file);
            return {
              filename: file.name,
              content: content,
            };
          }),
        )
      : undefined;

    const data = mapData(input, attachments, subject);

    if (input.customerId) {
      return postCustomerInquiry({
        businessUnit: businessUnit,
        customerId: input.customerId,
        label,
        recaptchaEnterpriseToken: input.recaptchaToken,
        requestBody: { data },
      });
    } else {
      return postInquiry({
        businessUnit: businessUnit,
        label,
        recaptchaEnterpriseToken: input.recaptchaToken,
        requestBody: { data },
      });
    }
  };
};

const mapData = (
  input: FormFields,
  attachments: Forms_CustomerInquiryRequest['attachments'],
  subject: string,
): Forms_CustomerInquiryRequest => ({
  subject,
  submittedText: input.inquiryContent,
  inquiryType: input.inquiryType,
  attachments,
  person: {
    name: input.firstName,
    surnamePreposition: input.surnamePreposition,
    surname: input.surname,
    gender: input.salutation,
    contact: {
      email: input.emailAddress,
      phoneNumber: input.phoneNumber,
    },
  },
  ...(input.authorIsBudgetManager
    ? {
        budgetManager: {
          companyName: input.budgetManagerBusiness,
          name: input.budgetManagerFirstname,
          surnamePreposition: input.budgetManagerSurnamePreposition,
          surname: input.budgetManagerSurname,
          gender: input.budgetManagerSalutation,
          address: {
            postalCode: input.budgetManagerPostalCode,
            houseNumber: input.budgetManagerHouseNumber,
            houseNumberSuffix: input.budgetManagerHouseNumberSuffix,
            street: input.budgetManagerStreetName,
            city: input.budgetManagerCity,
          },
          contact: { email: input.budgetManagerEmailAddress, phoneNumber: input.budgetManagerPhoneNumber },
        },
      }
    : {}),
  customerId: input.customerId,
});
